import { useState, useCallback } from 'react';
import { Bell, Loader2, X, Trash2, CheckCheck, Info, Upload, Pencil, Trash, Radio } from 'lucide-react';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { ScrollArea } from './ui/scroll-area';
import {
  useListNotificationsQuery,
  useListStationNotificationsQuery,
  useListAllNotificationsQuery,
  useListNotificationsPaginatedQuery,
  useListStationNotificationsPaginatedQuery,
  useListAllNotificationsPaginatedQuery,
  useMarkNotificationReadMutation,
  useMarkAllNotificationsReadMutation,
  useMarkAllStationNotificationsReadMutation,
  useMarkAllGlobalNotificationsReadMutation,
  useDeleteNotificationMutation,
  useClearChannelNotificationsMutation,
  useClearStationNotificationsMutation,
  useClearAllNotificationsMutation,
  PaginatedResponse,
} from '../../store/api';

const PAGE_SIZE = 5;

interface NotificationBellProps {
  channelId?: number;
  stationId?: number;
  /** Show all notifications across the system (admin view) */
  global?: boolean;
  /** Use "dark" for the dark-themed channel workspace header */
  variant?: 'light' | 'dark';
}

/** Pick an icon per notification type */
function NotifIcon({ type, className }: { type: string; className?: string }) {
  switch (type) {
    case 'track_uploaded':
      return <Upload className={className} />;
    case 'program_created':
      return <Radio className={className} />;
    case 'program_updated':
      return <Pencil className={className} />;
    case 'program_deleted':
      return <Trash className={className} />;
    default:
      return <Info className={className} />;
  }
}

export function NotificationBell({ channelId, stationId, global: isGlobal, variant = 'light' }: NotificationBellProps) {
  const [open, setOpen] = useState(false);
  const [pageOffset, setPageOffset] = useState(0);

  // ── Non-paginated queries for badge unread count (polling, always active) ──
  const channelCountResult = useListNotificationsQuery(channelId!, {
    pollingInterval: 60_000,
    skip: !channelId,
  });
  const stationCountResult = useListStationNotificationsQuery(stationId!, {
    pollingInterval: 60_000,
    skip: !stationId,
  });
  const globalCountResult = useListAllNotificationsQuery(undefined, {
    pollingInterval: 60_000,
    skip: !isGlobal,
  });

  const activeCountResult = channelId ? channelCountResult : stationId ? stationCountResult : globalCountResult;
  const allNotifications = (activeCountResult.data ?? []) as { isRead: boolean }[];
  const totalCount = allNotifications.length;
  const unreadCount = allNotifications.filter((n) => !n.isRead).length;

  // ── Paginated queries for popover list (NO polling, only when open) ──
  const channelResult = useListNotificationsPaginatedQuery(
    { channelId: channelId!, offset: pageOffset, limit: PAGE_SIZE },
    { skip: !channelId || !open },
  );
  const stationResult = useListStationNotificationsPaginatedQuery(
    { stationId: stationId!, offset: pageOffset, limit: PAGE_SIZE },
    { skip: !stationId || !open },
  );
  const globalResult = useListAllNotificationsPaginatedQuery(
    { offset: pageOffset, limit: PAGE_SIZE },
    { skip: !isGlobal || !open },
  );

  const activeResult = channelId ? channelResult : stationId ? stationResult : globalResult;
  const paginatedData = activeResult.data as PaginatedResponse | undefined;
  const notifications = (paginatedData?.items ?? []) as {
    id: number; channelId: number; type: string; title: string;
    message: string | null; isRead: boolean; createdAt: string;
  }[];
  const hasMore = paginatedData?.hasMore ?? false;
  const isFetching = activeResult.isFetching;

  const handleLoadMore = useCallback(() => {
    setPageOffset((prev) => prev + PAGE_SIZE);
  }, []);

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen) {
      setPageOffset(0);
    }
  };

  // ── Mark-read mutations ──
  const [markRead] = useMarkNotificationReadMutation();
  const [markAllReadChannel] = useMarkAllNotificationsReadMutation();
  const [markAllReadStation] = useMarkAllStationNotificationsReadMutation();
  const [markAllReadGlobal] = useMarkAllGlobalNotificationsReadMutation();

  const handleMarkAllRead = () => {
    setPageOffset(0);
    if (channelId) markAllReadChannel(channelId);
    else if (stationId) markAllReadStation(stationId);
    else markAllReadGlobal();
  };

  const handleMarkRead = (id: number) => {
    setPageOffset(0);
    markRead(id);
  };

  // ── Delete mutations ──
  const [deleteNotification] = useDeleteNotificationMutation();
  const [clearChannel] = useClearChannelNotificationsMutation();
  const [clearStation] = useClearStationNotificationsMutation();
  const [clearAll] = useClearAllNotificationsMutation();

  const handleDeleteOne = (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    setPageOffset(0);
    deleteNotification(id);
  };

  const handleClearAll = () => {
    setPageOffset(0);
    if (channelId) clearChannel(channelId);
    else if (stationId) clearStation(stationId);
    else clearAll();
  };

  const isDark = variant === 'dark';

  const formatTime = (iso: string) => {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDay = Math.floor(diffHr / 24);
    return `${diffDay}d ago`;
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={`relative ${isDark ? 'text-gray-300 hover:text-white hover:bg-gray-800' : 'text-gray-600 hover:text-gray-900'}`}
        >
          <Bell className="size-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-1.5 -right-1.5 flex size-[18px] items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white ring-2 ring-gray-900">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        className={`w-[360px] p-0 rounded-xl shadow-2xl ${isDark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}`}
      >
        {/* Header */}
        <div className={`flex items-center justify-between px-4 py-3 border-b ${isDark ? 'border-gray-700/80' : 'border-gray-100'}`}>
          <div className="flex items-center gap-2">
            <Bell className={`size-4 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
            <span className={`font-semibold text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Notifications
            </span>
            {totalCount > 0 && (
              <span className={`text-[11px] px-1.5 py-0.5 rounded-full font-medium ${isDark ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
                {totalCount}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                title="Mark all as read"
                className={`p-1.5 rounded-md transition-colors ${isDark ? 'text-blue-400 hover:bg-gray-800' : 'text-blue-600 hover:bg-blue-50'}`}
              >
                <CheckCheck className="size-3.5" />
              </button>
            )}
            {totalCount > 0 && (
              <button
                onClick={handleClearAll}
                title="Clear all notifications"
                className={`p-1.5 rounded-md transition-colors ${isDark ? 'text-red-400 hover:bg-gray-800' : 'text-red-500 hover:bg-red-50'}`}
              >
                <Trash2 className="size-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* List */}
        <ScrollArea className="max-h-[400px]">
          {notifications.length === 0 && !isFetching ? (
            <div className={`flex flex-col items-center justify-center py-10 px-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              <Bell className="size-10 mb-3 opacity-30" />
              <p className="text-sm font-medium">No notifications yet</p>
              <p className="text-xs mt-1 opacity-70">You're all caught up!</p>
            </div>
          ) : notifications.length === 0 && isFetching ? (
            <div className="flex justify-center py-10">
              <Loader2 className="size-5 animate-spin text-gray-500" />
            </div>
          ) : (
            <ul className="py-1">
              {notifications.map((n) => (
                <li
                  key={n.id}
                  className={`group relative px-3 py-2.5 mx-1 my-0.5 rounded-lg cursor-pointer transition-colors ${
                    isDark
                      ? `${n.isRead ? 'hover:bg-gray-800/60' : 'bg-blue-950/30 hover:bg-blue-950/50'}`
                      : `${n.isRead ? 'hover:bg-gray-50' : 'bg-blue-50/60 hover:bg-blue-50'}`
                  }`}
                  onClick={() => {
                    if (!n.isRead) handleMarkRead(n.id);
                  }}
                >
                  <div className="flex items-start gap-2.5">
                    {/* Type icon */}
                    <div className={`mt-0.5 flex-shrink-0 size-7 rounded-full flex items-center justify-center ${
                      isDark ? 'bg-gray-800' : 'bg-gray-100'
                    }`}>
                      <NotifIcon type={n.type} className={`size-3.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-[13px] leading-snug ${n.isRead
                          ? (isDark ? 'text-gray-400' : 'text-gray-600')
                          : (isDark ? 'text-gray-100 font-medium' : 'text-gray-900 font-medium')
                        }`}>
                          {n.title}
                        </p>
                        {/* Delete × button */}
                        <button
                          onClick={(e) => handleDeleteOne(e, n.id)}
                          title="Remove notification"
                          className={`flex-shrink-0 opacity-0 group-hover:opacity-100 p-0.5 rounded transition-all ${
                            isDark ? 'text-gray-500 hover:text-red-400 hover:bg-gray-800' : 'text-gray-400 hover:text-red-500 hover:bg-red-50'
                          }`}
                        >
                          <X className="size-3.5" />
                        </button>
                      </div>
                      {n.message && (
                        <p className={`text-xs mt-0.5 leading-relaxed ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                          {n.message}
                        </p>
                      )}
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`text-[11px] ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
                          {formatTime(n.createdAt)}
                        </span>
                        {!n.isRead && (
                          <span className="size-1.5 rounded-full bg-blue-500" />
                        )}
                      </div>
                    </div>
                  </div>
                </li>
              ))}

              {/* Load more */}
              {hasMore && (
                <li className="px-3 py-2.5 text-center">
                  <button
                    onClick={handleLoadMore}
                    disabled={isFetching}
                    className={`text-xs font-medium px-3 py-1.5 rounded-md transition-colors disabled:opacity-50 flex items-center gap-1.5 mx-auto ${
                      isDark ? 'text-blue-400 hover:bg-gray-800' : 'text-blue-600 hover:bg-blue-50'
                    }`}
                  >
                    {isFetching ? <Loader2 className="size-3 animate-spin" /> : null}
                    Load more
                  </button>
                </li>
              )}
            </ul>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
